import { Date_toDateString, Date_toDateTimeString } from "../../../common/dateUtils";
import { FormState_PopulateErrorMessages } from "../../../common/formStateUtils";
import { String_removePrefix } from "../../../common/stringUtils";
import HotWorkOnShipPermitRequest from "../../requests/HotWorkOnShipPermitRequest";
import Errors from "../../responses/Errors";
import FormElement, { formElement } from "../FormElement";
import PersonFormElement, {
    PersonFormElement_default,
    PersonFormElement_populateErrorMessages,
    PersonFormElement_toPerson
} from "../PersonFormElement";
import PositionPickerRow, {
    PositionPickerRow_default,
    PositionPickerRow_toPosition,
    PositionPickerRows_populateErrorMessages
} from "./PositionPickerRow";

type HotWorkOnShipPermitFormState = {
    remindMe: FormElement<boolean>;
    remindMeDate: FormElement<Date>;
    startDate: FormElement<Date>;
    endDate: FormElement<Date>;
    adminComment: FormElement<string>;
    description: FormElement<string>;
    positions: PositionPickerRow[];
    expirationReminderEmail: FormElement<string>;
    expirationReminderPhoneNumber: FormElement<string>;
    commanderOrRepresentative: PersonFormElement;
    shipName: FormElement<string>;
    callSign: FormElement<string>;
    terminalAcceptsWork: FormElement<boolean>;
    ehCoordinatorAcceptsWork: FormElement<boolean>;
    anonymousUserEmail: FormElement<string>;
};

export default HotWorkOnShipPermitFormState;

export function HotWorkOnShipPermitFormState_default(): HotWorkOnShipPermitFormState {
    const permit: HotWorkOnShipPermitFormState = {
        adminComment: formElement(""),
        anonymousUserEmail: formElement(""),
        callSign: formElement(""),
        commanderOrRepresentative: PersonFormElement_default(),
        description: formElement(""),
        ehCoordinatorAcceptsWork: formElement(false),
        endDate: formElement(new Date()),
        expirationReminderEmail: formElement(""),
        expirationReminderPhoneNumber: formElement(""),
        positions: [PositionPickerRow_default()],
        remindMe: formElement(false),
        remindMeDate: formElement(new Date()),
        shipName: formElement(""),
        startDate: formElement(new Date()),
        terminalAcceptsWork: formElement(false)
    };

    return permit;
}

export function HotWorkOnShipPermitFormState_toRequest(
    formState: HotWorkOnShipPermitFormState
): HotWorkOnShipPermitRequest {
    const request: HotWorkOnShipPermitRequest = {
        adminComment: formState.adminComment.value,
        anonymousUserEmail: formState.anonymousUserEmail.value,
        callSign: formState.callSign.value,
        commanderOrRepresentative: PersonFormElement_toPerson(formState.commanderOrRepresentative),
        description: formState.description.value,
        ehCoordinatorAcceptsWork: formState.ehCoordinatorAcceptsWork.value,
        endDate: Date_toDateTimeString(formState.endDate.value),
        expirationReminderEmail: formState.expirationReminderEmail.value,
        expirationReminderPhoneNumber: formState.expirationReminderPhoneNumber.value,
        positions: formState.positions.map(PositionPickerRow_toPosition),
        remindMe: formState.remindMe.value,
        remindMeDate: Date_toDateString(formState.remindMeDate.value),
        shipName: formState.shipName.value,
        startDate: Date_toDateTimeString(formState.startDate.value),
        terminalAcceptsWork: formState.terminalAcceptsWork.value
    };

    return request;
}

export function HotWorkOnShipPermitFormState_populateErrorMessages(
    formState: HotWorkOnShipPermitFormState,
    errors: Errors
): HotWorkOnShipPermitFormState {
    const errorKeys = Object.keys(errors);

    const positionsErrorKeys = errorKeys.filter((e) => e.startsWith("Positions"));
    const positionErrors = Object.fromEntries(
        positionsErrorKeys.map((k) => [String_removePrefix(k, "Positions"), errors[k]])
    );

    const commanderOrRepresentativeErrorKeys = errorKeys.filter((e) => e.startsWith("CommanderOrRepresentative."));
    const commanderOrRepresentativeErrors = Object.fromEntries(
        commanderOrRepresentativeErrorKeys.map((k) => [String_removePrefix(k, "CommanderOrRepresentative."), errors[k]])
    );

    const updatedFormState = FormState_PopulateErrorMessages(formState, errors);
    updatedFormState.positions = PositionPickerRows_populateErrorMessages(formState.positions, positionErrors);

    updatedFormState.commanderOrRepresentative = PersonFormElement_populateErrorMessages(
        formState.commanderOrRepresentative,
        commanderOrRepresentativeErrors
    );

    return updatedFormState;
}
